import {
  Button,
  Card,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { Protected, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useEffect, useState } from "react";
import { Bar, BarChart, Pie, PieChart, ResponsiveContainer, XAxis, YAxis } from "recharts";

export default function Dashboard() {
  return (
    <Protected redirectTo="/login">
      <Content />
    </Protected>
  );
}

function Content() {
  const authFetch = useAuthenticatedFetch();
  type SummaryRow = { stage: string; source?: string; count: number; total_cents: number };
  const [data, setData] = useState<{ bySource: SummaryRow[]; byStage: SummaryRow[] } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch("/api/reports/summary");
      const json = (await res.json()) as { bySource: SummaryRow[]; byStage: SummaryRow[] };
      setData(json);
    })();
  }, [authFetch]);

  const leadsBySource = (data?.bySource ?? []).map((d) => ({
    label: (d.source || "unknown").toString(),
    value: Number(d.count || 0),
  }));

  const forecast = (data?.byStage ?? []).map((d) => ({
    label: d.stage || "",
    value: Number(d.total_cents || 0) / 100,
  }));

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat title="Total Leads" value={sumCounts(data?.byStage)} />
        <Stat title="Open Pipeline ($)" value={formatCurrency(sumOpen(data?.byStage))} />
        <Stat title="Won ($)" value={formatCurrency(sumWon(data?.byStage))} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4 space-y-4">
          <h3 className="font-semibold">Leads by Source</h3>
          <ChartContainer config={{ value: { label: "Leads" } }}>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={leadsBySource} dataKey="value" nameKey="label" fill="var(--primary)" />
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
        <Card className="p-4 space-y-4">
          <h3 className="font-semibold">Revenue Forecast by Stage</h3>
          <ChartContainer config={{ value: { label: "Revenue" } }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={forecast}>
                <XAxis dataKey="label" hide />
                <YAxis hide />
                <Bar dataKey="value" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                <ChartTooltip content={<ChartTooltipContent />} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>
      </div>

      <div className="flex gap-3">
        <Button asChild variant="outline">
          <a href="/api/export/contacts.csv">Export Contacts</a>
        </Button>
        <Button asChild variant="outline">
          <a href="/api/export/leads.csv">Export Leads</a>
        </Button>
      </div>

      <Tabs defaultValue="notes" className="mt-4">
        <TabsList>
          <TabsTrigger value="notes">Quick Tips</TabsTrigger>
          <TabsTrigger value="about">About</TabsTrigger>
        </TabsList>
        <TabsContent value="notes" className="text-sm text-muted-foreground">
          Use Contacts to store people and companies. Create manual Leads and progress them through
          stages: New → Discovery → Proposal → Negotiation → Won/Lost.
        </TabsContent>
        <TabsContent value="about" className="text-sm text-muted-foreground">
          Single-user CRM. No auto email sync. Simple and fast.
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ title, value }: { title: string; value: string | number }) {
  return (
    <Card className="p-4">
      <div className="text-sm text-muted-foreground">{title}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </Card>
  );
}

function sumCounts(rows?: Array<{ count: number }> | null) {
  if (!rows) return 0;
  return rows.reduce((a: number, r) => a + Number(r.count || 0), 0);
}
function sumOpen(rows?: Array<{ stage: string; total_cents: number }> | null) {
  if (!rows) return 0;
  const open = ["new", "discovery", "proposal", "negotiation"];
  return (
    rows
      .filter((r) => open.includes(r.stage))
      .reduce((a: number, r) => a + Number(r.total_cents || 0), 0) / 100
  );
}
function sumWon(rows?: Array<{ stage: string; total_cents: number }> | null) {
  if (!rows) return 0;
  return (
    rows
      .filter((r) => r.stage === "won")
      .reduce((a: number, r) => a + Number(r.total_cents || 0), 0) / 100
  );
}

function formatCurrency(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}
